import {ChangeEvent, FC, KeyboardEvent, useLayoutEffect, useRef, useState} from 'react';
import {TextInput} from 'features/registration/ui/components/TextInput';
import {extractAccountDigits, formatAccountDisplay, getAccountNumberError} from '../../model/accountNumber';
import styles from './AccountNumberStep.module.css';

type Props = {
	value: string;
	bik: string;
	onChange: (accountDigits: string) => void;
};

const caretPosAfterDigits = (display: string, digitCount: number): number => {
	if (digitCount <= 0) {
		return 0;
	}

	let seen = 0;

	for (let i = 0; i < display.length; i++) {
		if (/\d/.test(display[i]!)) {
			seen += 1;

			if (seen === digitCount) {
				return i + 1;
			}
		}
	}

	return display.length;
};

export const AccountNumberStep: FC<Props> = ({value, bik, onChange}) => {
	const accountDigits = extractAccountDigits(value);
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [caretDigits, setCaretDigits] = useState<number | null>(null);
	const displayError = getAccountNumberError(value, bik);

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		const input = event.target;
		const nextRaw = input.value;
		const nextAccount = extractAccountDigits(nextRaw);
		const caret = input.selectionStart ?? nextRaw.length;
		const digitsBeforeCaret = extractAccountDigits(nextRaw.slice(0, caret)).length;

		onChange(nextAccount);
		setCaretDigits(digitsBeforeCaret);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== 'Backspace') {
			return;
		}

		const input = event.currentTarget;
		const caret = input.selectionStart ?? 0;
		const selectionEnd = input.selectionEnd ?? 0;

		if (caret !== selectionEnd || caret === 0) {
			return;
		}

		const display = formatAccountDisplay(accountDigits);
		const prevChar = display[caret - 1];

		if (prevChar !== ' ') {
			return;
		}

		event.preventDefault();

		const digitsBeforeCaret = extractAccountDigits(display.slice(0, caret)).length;

		if (digitsBeforeCaret === 0) {
			return;
		}

		const nextDigits = accountDigits.slice(0, digitsBeforeCaret - 1) + accountDigits.slice(digitsBeforeCaret);

		onChange(nextDigits);
		setCaretDigits(digitsBeforeCaret - 1);
	};

	useLayoutEffect(() => {
		if (caretDigits === null || !inputRef.current) {
			return;
		}

		const pos = caretPosAfterDigits(formatAccountDisplay(accountDigits), caretDigits);
		inputRef.current.setSelectionRange(pos, pos);
		setCaretDigits(null);
	}, [accountDigits, caretDigits]);

	return (
		<div className={styles.root}>
			<h2 className={styles.title}>Введите номер счёта</h2>

			<div className={styles.field}>
				<TextInput
					ref={inputRef}
					name="paymentAccount"
					className={styles.input}
					value={formatAccountDisplay(value)}
					placeholder="00000 00000 00000 00000"
					inputMode="numeric"
					autoComplete="off"
					autoFocus
					aria-invalid={Boolean(displayError)}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onClear={() => onChange('')}
				/>
				{displayError ? <p className={styles.error}>{displayError}</p> : null}
			</div>
		</div>
	);
};
